import { useEffect, useRef } from 'react';
import { useLanguage } from '../hooks/useLanguage';

const COPY = {
  en: {
    title: 'Your privacy on the UK Web Archive',
    body: 'We use a single Google Analytics 4 cookie to understand how visitors use our site so we can improve it. No personal data is sold or shared with advertisers. Essential cookies are always on.',
    accept: 'Accept analytics cookies',
    reject: 'Reject non-essential',
    learnMore: 'Cookie policy',
    newTab: '(opens in a new tab)',
    close: 'Close',
    badge: 'Cookies',
  },
  cy: {
    title: "Eich preifatrwydd ar Archif We'r DU",
    body: "Rydym yn defnyddio un cwci Google Analytics 4 i ddeall sut mae ymwelwyr yn defnyddio ein gwefan er mwyn ei gwella. Ni werthir nac yn rhannu data personol gyda hysbysebwyr. Mae cwcis hanfodol bob amser ymlaen.",
    accept: 'Derbyn cwcis dadansoddeg',
    reject: 'Gwrthod rhai dewisol',
    learnMore: 'Polisi cwcis',
    newTab: '(yn agor mewn tab newydd)',
    close: 'Cau',
    badge: 'Cwcis',
  },
  gd: {
    title: 'Do phrìobhaideachd air Tasglann Lìn na RA',
    body: "Bidh sinn a' cleachdadh aon chriomag Google Analytics 4 gus tuigsinn mar a chleachdas luchd-tadhail an làrach gus a leasachadh. Cha tèid dàta pearsanta a reic no a roinn le sanasairean. Tha criomagan riatanach an-còmhnaidh air.",
    accept: 'Gabh ri criomagan sgrùdaidh',
    reject: 'Diùlt feadhainn neo-riatanach',
    learnMore: 'Poileasaidh chriomagan',
    newTab: '(fosglaidh seo ann an taba ùr)',
    close: 'Dùin',
    badge: 'Criomagan',
  },
};

// Where the British Library hosts its cookie policy.
const COOKIE_POLICY_URL = 'https://join-britishlibrary.co.uk/cookie-policy/';

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]';

function CookieIcon() {
  return (
    <svg aria-hidden="true" focusable="false" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M12 2a10 10 0 102.5 19.7A6 6 0 0112 14a3 3 0 01-3-3 5 5 0 01-3-4.6A10 10 0 0112 2z" />
      <circle cx="9" cy="11" r="0.8" fill="currentColor" />
      <circle cx="13.5" cy="9" r="0.8" fill="currentColor" />
      <circle cx="14" cy="14" r="0.8" fill="currentColor" />
      <circle cx="10" cy="16" r="0.8" fill="currentColor" />
    </svg>
  );
}

export default function CookieBanner({ open, onAccept, onReject, onClose }) {
  const lang = useLanguage();
  const t = COPY[lang.code] || COPY.en;
  const dialogRef = useRef(null);

  // Move focus to the start of the banner when it opens, so the first Tab
  // reaches Close and focus then follows the visual order. Don't move focus
  // on every render. When the banner closes, focus goes back to whatever had
  // it before (e.g. "Manage cookies"); on first load nothing had focus, so it
  // goes to the start of the page — the next Tab then reaches "Skip to main
  // content" rather than skipping past it (WCAG 2.4.1).
  //
  // While it is open the rest of the page is inert: aria-modal alone does not
  // stop VoiceOver iOS / TalkBack swiping to the content behind (SR-10).
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const background = [...document.querySelectorAll('.skip-link, header, #main-content, footer')];
    background.forEach((el) => el.setAttribute('inert', ''));
    dialogRef.current?.focus();
    return () => {
      background.forEach((el) => el.removeAttribute('inert'));
      const target =
        previous && previous !== document.body && document.contains(previous)
          ? previous
          : document.getElementById('page-start');
      target?.focus?.({ preventScroll: true });
    };
  }, [open]);

  // Esc dismisses the banner without making a choice (treated like Close).
  // Handle every Tab step, not just wrapping at the ends: some browsers'
  // keyboard preferences skip native buttons/links. With an inert background
  // that can send focus straight to browser chrome instead of the next action.
  // Keep the same DOM order for Tab / Shift+Tab (WCAG 2.4.3 Focus Order).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || e.ctrlKey || e.metaKey) return;
      const node = dialogRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.tabIndex >= 0 && !el.matches(':disabled') &&
          !el.closest('[inert]') && el.getClientRects().length > 0 &&
          getComputedStyle(el).visibility === 'visible'
      );
      e.preventDefault();
      if (items.length === 0) {
        node.focus({ preventScroll: true });
        return;
      }
      const current = items.indexOf(document.activeElement);
      const next = current < 0
        ? (e.shiftKey ? items.length - 1 : 0)
        : (current + (e.shiftKey ? -1 : 1) + items.length) % items.length;
      items[next].focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-body"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 pointer-events-none focus:outline-none"
    >
      <div className="max-w-5xl mx-auto pointer-events-auto">
        <div className="relative rounded-2xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 shadow-2xl ring-1 ring-black/5 overflow-hidden">
          {/* Close — first in the DOM so the Tab order is Close → Cookie policy
              → Reject → Accept, matching the visual top-right → bottom reading
              order (WCAG 2.4.3). */}
          <button
            type="button"
            aria-label={t.close}
            onClick={onClose}
            className="absolute top-2 right-2 p-1.5 rounded-md text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
          >
            <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 pr-12 md:p-5 md:pr-12">
            {/* Icon column */}
            <div className="flex items-start md:items-center gap-3 md:flex-shrink-0">
              <span aria-hidden="true" className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-accent-primary/10 text-accent-text">
                <CookieIcon />
              </span>
            </div>

            {/* Body — the title is rendered once so aria-labelledby has a
                single, unambiguous target. */}
            <div className="flex-1 min-w-0">
              <p
                id="cookie-banner-title"
                className="text-sm font-semibold text-gray-900 dark:text-dark-100"
              >
                {t.title}
              </p>
              <p
                id="cookie-banner-body"
                className="mt-1 text-xs md:text-sm text-gray-600 dark:text-dark-300 leading-relaxed"
              >
                {t.body}{' '}
                <a
                  href={COOKIE_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-accent-text hover:underline underline-offset-2"
                >
                  {t.learnMore}
                  <span aria-hidden="true" className="ml-0.5">↗</span>
                  <span className="sr-only"> {t.newTab}</span>
                </a>
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 md:flex-shrink-0">
              <button
                type="button"
                onClick={onReject}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-dark-200 bg-gray-100 dark:bg-dark-800 hover:bg-gray-200 dark:hover:bg-dark-700 border border-gray-200 dark:border-dark-700 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2"
              >
                {t.reject}
              </button>
              <button
                type="button"
                onClick={onAccept}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
              >
                {t.accept}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
